"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import TypewriterEffect from "@/components/TypewriterEffect";
import { Button } from "@/components/ui/button";

interface CodingIntroProps {
  /**
   * true 면 타자기 애니메이션 없이 전체 내용을 정적으로 즉시 노출 (이미 본 적 있는 재방문).
   * false(기본)면 첫 방문 — 위에서부터 타자기 애니메이션으로 펼친다. JCG-2099.
   * 인트로는 숨겨지지 않고 항상 보여진 상태로 유지된다(숨기기 버튼 없음).
   */
  revealed?: boolean;
}

export default function CodingIntro({ revealed = false }: CodingIntroProps) {
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [isImpatient, setIsImpatient] = useState(false);

  const handleComplete = useCallback(() => {
    setVisibleIndex((prev) => prev + 1);
  }, []);

  const handleImpatientClick = () => {
    setIsImpatient(true);
  };

  const elements = useMemo(
    () => [
      <TypewriterEffect
        key={0}
        className="text-center text-base mt-12"
        text="코딩? 프로그래밍? 비전공자한테는 너무 막막하죠..."
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={1}
        className="text-center text-base mt-12"
        text="그래서 코딩은 그냥 포기하려고 하셨나요?"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={2}
        className="text-center text-base mt-12"
        text="잠깐만요, 코딩은 이론이랑 공부법이 완전히 달라요."
        onComplete={handleComplete}
      />,
      <p key={3} className="text-center text-2xl mt-12">
        이론은 나올 곳을 찍을 수 있지만, 코딩은 기초부터 탄탄히 쌓아야 해요
      </p>,
      <Image
        key={4}
        className="mt-12 mb-12 mx-auto"
        src="/theory/dodongtak_gamza.webp"
        alt="shock"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={5}
        className="text-center text-base mt-4"
        text="이론처럼 '나올 부분만 외워서 찍기'는 코딩엔 통하지 않거든요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={6}
        className="text-center text-base mt-12"
        text="코드를 직접 이해하고 한 줄씩 따라갈 수 있어야 풀려요"
        onComplete={handleComplete}
      />,
      <p key={7} className="text-center text-base font-semibold mt-8">
        그럼 너무 어려운 거 아니에요?
      </p>,
      <Image
        key={8}
        className="mt-8 mx-auto"
        src="/theory/yesyes_gamza.webp"
        alt="yes"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={9}
        className="text-center text-base mt-4"
        text="괜찮아요. 코딩은 기초를 제대로 이해하고 손에 익히는 게 핵심이에요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={10}
        className="text-center text-2xl font-semibold mt-4"
        text="변수·연산자·반복문·배열·포인터..."
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={11}
        className="text-center text-base mt-4"
        text="이 기초들만 차근차근 제대로 익히면, 어떤 문제든 응용해서 풀 수 있어요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={12}
        className="text-center text-2xl text-destructive font-semibold mt-4"
        text="코딩은 외우는 게 아니라 '이해하고 응용하는 힘'이 핵심이에요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={13}
        className="text-center text-base mt-4"
        text="그래서 기초를 쌓는 데 시간이 조금 걸려요"
        onComplete={handleComplete}
      />,
      <Image
        key={14}
        className="mt-8 mx-auto"
        src="/theory/pride_gamza.webp"
        alt="pride"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={15}
        className="text-center text-base mt-4"
        text="대신 기초가 한 번 단단해지면 코딩 점수는 안정적으로 챙길 수 있어요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={16}
        className="text-center text-2xl font-semibold mt-12"
        text="그래서 감자가 기초 하나하나를 아주 자세하게 적어놨어요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={17}
        className="text-center text-base mt-4"
        text="코딩을 처음 접하는 분도 이해할 수 있도록, 그림과 함께 차근차근 풀어 설명했어요"
        onComplete={handleComplete}
      />,
      <Image
        key={18}
        className="mt-8 mx-auto"
        src="/theory/gamdalssam_gamza.webp"
        alt="advice"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={19}
        className="text-center text-base mt-4"
        text="조급해하지 말고, 기초부터 한 단계씩 같이 쌓아가요"
        onComplete={handleComplete}
      />,
      <p
        key={20}
        className="text-center text-lg font-semibold text-destructive mt-12"
      >
        기초를 제대로 쌓아서 어떤 코딩 문제든 응용해 풀 수 있게 되는 게 목표예요
      </p>,
      <TypewriterEffect
        key={21}
        className="text-center text-base mt-4"
        text="기초가 단단하면 합격은 자연스럽게 따라와요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={22}
        className="text-center text-base mt-4"
        text="급하게 외우는 공부 대신, 제대로 이해하는 공부를 해봐요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={23}
        className="text-center text-base mt-4"
        text="그럼 코딩 기초부터 차근차근 시작해보자구요!"
        onComplete={handleComplete}
      />,
    ],
    [handleComplete]
  );

  // 전체 내용을 타자기 없이 정적으로 나열(재방문·건너뛰기 공용).
  const staticElements = useMemo(
    () =>
      elements.map((element) => {
        if (element.type === TypewriterEffect) {
          return (
            <p key={element.key} className={element.props.className}>
              {element.props.text}
            </p>
          );
        }
        return element;
      }),
    [elements]
  );

  useEffect(() => {
    // 재방문(revealed)·건너뛰기(isImpatient)·완료 시 애니메이션 타이머 없음.
    if (revealed || isImpatient || visibleIndex >= elements.length) {
      return;
    }

    const currentElement = elements[visibleIndex];
    if (
      currentElement &&
      (currentElement.type === "p" || currentElement.type === Image)
    ) {
      const timer = setTimeout(handleComplete, 1000); // 1초 후에 다음 요소 표시
      return () => clearTimeout(timer);
    }
  }, [visibleIndex, elements, handleComplete, isImpatient, revealed]);

  // 재방문(이미 본 상태) 또는 "답답혀" 건너뛰기 — 애니메이션 없이 전체 정적 노출, 숨기기 버튼 없음.
  if (revealed || isImpatient) {
    return <div className="px-4 mx-auto">{staticElements}</div>;
  }

  // 첫 방문 — 위에서부터 타자기 애니메이션. 완료되면 버튼 없이 전체가 그대로 유지됨(숨기지 않음).
  return (
    <div className="px-4 mx-auto">
      {elements.slice(0, visibleIndex + 1)}
      {visibleIndex < elements.length && (
        <div className="text-center mt-30">
          <Button onClick={handleImpatientClick} variant="outline">
            답답혀
          </Button>
        </div>
      )}
    </div>
  );
}
